import React from 'react'
import styled from 'styled-components'
import Plot from 'react-plotly.js'

import Card from 'components/Card'
import StatusButton, { StatusButtonProps } from 'pages/Dashboard/StatusButton'

import useScreenSize from 'hooks/useScreenSize'

import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'

type Props = {
  title: string
  description: string
  image?: string | null
  plotData?: Array<Plotly.Data> | null
  buttons: Array<StatusButtonProps>
  isLoaded?: boolean
  videoKey?: string | null
}

const CardWithButtons = ({ title, description, image = null, plotData = null, buttons, isLoaded = true, videoKey = null }: Props) => {
  const screenSize = useScreenSize()

  const imageWidth = screenSize === 'small' ? 70 : 90

  return (
    <Card title={title} isLoaded={isLoaded} videoKey={videoKey}>
      <S.Description>{description}</S.Description>

      <S.ImageAndButtons>
        {image && <S.Image src={image} imageWidth={imageWidth} />}
        {plotData &&
          <S.Plot
            data={plotData}
            layout={{
              height: imageWidth,
              width: imageWidth,
              showlegend: false,
              margin: { t: 0, b: 0, l: 0, r: 0 }
            }}
            config={{
              displayModeBar: false,
              showLink: false
            }}
          />
        }
        <S.Buttons>
          {buttons.map(button => <StatusButton {...button} key={button.text} />)}
        </S.Buttons>
      </S.ImageAndButtons>
    </Card>
  )
}

export default CardWithButtons

const S = {
  Description: styled.div`
    font-size: 10pt;
    font-family: inter.regular;
    color: ${color('cardDescription')};
    min-height: 60px;

    @media ${screenSize.small} {
      min-height: unset;
    }
  `,
  ImageAndButtons: styled.div`
    display: flex;
    margin-top: 17px;
  `,
  Image: styled.img.withConfig({
    shouldForwardProp: (prop) => !['imageWidth'].includes(prop)
  })<{ imageWidth: number }>`
    display: inline-block;
    height: ${({ imageWidth }) => imageWidth}px;
    width: ${({ imageWidth }) => imageWidth}px;
  `,
  Plot: styled(Plot)`
    display: inline-block;
  `,
  Buttons: styled.div`
    display: inline-flex;
    flex: 1;
    flex-direction: column;
    margin-left: 20px;

    @media ${screenSize.small} {
      margin-left: 15px;
    }

    > *:not(:first-child) {
      margin-top: 10px;
    }
  `
}
